import type {CommitsProps} from '@github-ui/commits/shared/Commits'
import type {ConversationComment} from '../../../page-data/payloads/conversation-comments'
import {CommentsSort} from '../use-comments-sort-state'

type CommitGroup = CommitsProps['commitGroups'][number]
type Commit = CommitGroup['commits'][number]

export interface ConversationCommitGroup {
  type: 'commits'
  commitGroups: CommitGroup[]
}

function commitTimestamp(commit: Commit) {
  const timestamp = Date.parse(commit.committedDate ?? commit.authoredDate ?? '')
  return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp
}

/** A commit at the same time as a comment follows that comment in chronological order. */
export function interleaveCommitGroups(
  comments: ConversationComment[],
  commitGroups: CommitGroup[],
  sort: CommentsSort,
): Array<ConversationComment | ConversationCommitGroup> {
  const chronologicalComments = sort === CommentsSort.NewestFirst ? comments.toReversed() : comments
  const slots: CommitGroup[][] = Array.from({length: comments.length + 1}, () => [])
  const commits = commitGroups
    .flatMap(group => group.commits.map(commit => ({commit, title: group.title})))
    .toSorted((a, b) => commitTimestamp(a.commit) - commitTimestamp(b.commit))
  if (sort === CommentsSort.NewestFirst) commits.reverse()

  for (const {commit, title} of commits) {
    const timestamp = commitTimestamp(commit)
    let start = 0
    let end = chronologicalComments.length
    while (start < end) {
      const middle = Math.floor((start + end) / 2)
      const comment = chronologicalComments[middle]
      if (comment && Date.parse(comment.createdAt) <= timestamp) start = middle + 1
      else end = middle
    }
    const slot = slots[sort === CommentsSort.NewestFirst ? comments.length - start : start]
    if (!slot) continue
    const lastGroup = slot.at(-1)
    if (lastGroup?.title === title) lastGroup.commits.push(commit)
    else slot.push({title, commits: [commit]})
  }
  const items: Array<ConversationComment | ConversationCommitGroup> = []
  for (const [index, groups] of slots.entries()) {
    if (groups.length > 0) items.push({type: 'commits', commitGroups: groups})
    const comment = comments[index]
    if (comment) items.push(comment)
  }
  return items
}
